import { useNavigate } from 'react-router-dom';
import {
  Stack,
  Paper,
  Group,
  Text,
  Title,
  SimpleGrid,
  Card,
  ThemeIcon,
  Button,
  Badge,
  Skeleton,
} from '@mantine/core';
import {
  IconToolsKitchen2,
  IconClipboardList,
  IconNotes,
  IconCalendarEvent,
  IconLeaf,
  IconArrowsExchange,
  IconSparkles,
} from '@tabler/icons-react';
import { useAuthStore } from '@/store/auth';
import { useMenus } from '@/hooks/api';

const TOOLS = [
  {
    title: 'My Menus',
    description: 'Create and manage menus with AI-powered ingredient mapping and quantity calculations.',
    icon: IconClipboardList,
    path: '/menu-planner/menus',
    color: 'blue',
  },
  {
    title: 'Recipe to Cart',
    description: 'Paste any recipe and instantly find matching products to add to your cart.',
    icon: IconNotes,
    path: '/menu-planner/recipe',
    color: 'grape',
  },
  {
    title: 'Event Planner',
    description: 'Plan a full event menu with quantities, costs, and a prep timeline.',
    icon: IconCalendarEvent,
    path: '/menu-planner/events',
    color: 'teal',
  },
  {
    title: 'Seasonal Picks',
    description: "See what's in season, what's coming soon, and what's ending soon.",
    icon: IconLeaf,
    path: '/menu-planner/seasonal',
    color: 'green',
  },
  {
    title: 'Substitution Finder',
    description: 'Find alternative products based on price, availability, or dietary needs.',
    icon: IconArrowsExchange,
    path: '/menu-planner/substitutes',
    color: 'orange',
  },
  {
    title: 'AI Menu Plan',
    description: 'Let AI design a complete menu based on cuisine, covers, and preferences.',
    icon: IconSparkles,
    path: '/menu-planner/menus',
    color: 'violet',
  },
];

export default function MenuPlannerHub() {
  const navigate = useNavigate();
  const customerId = useAuthStore((s) => s.user?.customer?.id);
  const { data: menusData, isLoading: loadingMenus } = useMenus(customerId);
  const recentMenus = menusData?.items?.slice(0, 3);

  return (
    <Stack gap="lg">
      {/* Hero Section */}
      <Paper
        p="xl"
        radius="md"
        style={{
          background: 'linear-gradient(135deg, var(--mantine-color-green-7) 0%, var(--mantine-color-brand-7) 100%)',
          color: 'white',
        }}
      >
        <Stack gap="md" align="center" ta="center" py="lg">
          <ThemeIcon size={64} radius="xl" variant="white" color="green">
            <IconToolsKitchen2 size={32} />
          </ThemeIcon>
          <Title order={2}>AI-Powered Menu Planning</Title>
          <Text size="lg" maw={600} style={{ opacity: 0.9 }}>
            Turn recipes into orders, plan events, discover seasonal products, and manage your menus
            — all powered by AI.
          </Text>
          <Group>
            <Button
              size="md"
              variant="white"
              color="dark"
              onClick={() => navigate('/menu-planner/recipe')}
            >
              Recipe to Cart
            </Button>
            <Button
              size="md"
              variant="outline"
              color="white"
              onClick={() => navigate('/menu-planner/events')}
              styles={{ root: { borderColor: 'white', color: 'white' } }}
            >
              Plan an Event
            </Button>
          </Group>
        </Stack>
      </Paper>

      {/* Tool Cards */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
        {TOOLS.map((tool) => (
          <Card
            key={tool.path + tool.title}
            shadow="sm"
            padding="lg"
            radius="md"
            withBorder
            onClick={() => navigate(tool.path)}
            style={{ cursor: 'pointer', transition: 'transform 150ms ease, box-shadow 150ms ease' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = 'var(--mantine-shadow-md)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = '';
              e.currentTarget.style.boxShadow = '';
            }}
          >
            <Stack gap="sm">
              <ThemeIcon size={40} radius="md" color={tool.color} variant="light">
                <tool.icon size={20} />
              </ThemeIcon>
              <Text fw={600}>{tool.title}</Text>
              <Text size="sm" c="dimmed" lh={1.5}>
                {tool.description}
              </Text>
            </Stack>
          </Card>
        ))}
      </SimpleGrid>

      {/* Recent Menus */}
      {(loadingMenus || (recentMenus && recentMenus.length > 0)) && (
        <Paper p="lg" radius="md" withBorder>
          <Stack gap="md">
            <Group justify="space-between">
              <Text fw={600} size="lg">Recent Menus</Text>
              <Button
                variant="subtle"
                size="sm"
                onClick={() => navigate('/menu-planner/menus')}
              >
                View All
              </Button>
            </Group>
            {loadingMenus ? (
              <Stack gap="xs">
                <Skeleton height={60} radius="md" />
                <Skeleton height={60} radius="md" />
                <Skeleton height={60} radius="md" />
              </Stack>
            ) : (
              <Stack gap="xs">
                {recentMenus?.map((menu) => (
                  <Paper
                    key={menu.id}
                    p="md"
                    radius="md"
                    withBorder
                    onClick={() => navigate(`/menu-planner/menus/${menu.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Group justify="space-between">
                      <div>
                        <Text fw={500}>{menu.name}</Text>
                        <Group gap="xs">
                          {menu.cuisine_type && (
                            <Badge size="sm" variant="light">{menu.cuisine_type}</Badge>
                          )}
                          <Text size="xs" c="dimmed">
                            {menu.cover_count} covers &middot; {menu.services_per_week}x/week
                          </Text>
                        </Group>
                      </div>
                      <Badge
                        color={menu.status === 'active' ? 'green' : 'gray'}
                        variant="light"
                      >
                        {menu.status}
                      </Badge>
                    </Group>
                  </Paper>
                ))}
              </Stack>
            )}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
